import { Setter } from 'libs/end-to-end/src/ui-types';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { TransformScriptContents } from 'libs/end-to-end/src/types/databases/transform/transform-script-contents';
import { PlatformTransformDetails } from 'libs/end-to-end/src/types/databases/transform/transform';
import { TransformReferenceWithOutputConsoleUrl } from 'libs/end-to-end/src/types/databases/transform/transform';
import { TransformReferencesPaginatedWithOutputConsoleUrls } from 'libs/end-to-end/src/types/databases/transform/transform';
import { Transform } from 'libs/end-to-end/src/types/databases/transform/transform';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { UserStatus } from 'apps/sigma2-ui/src/state/user-types';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useUserUploadHeadless } from 'apps/sigma2-ui/src/users/lib/useUserUploadHeadless';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { CreateTransformRequestBodySchema } from 'libs/end-to-end/src/types/databases/transform/create-transform-request-body';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { waitForMs } from 'libs/end-to-end/src/generic';
import { DatabaseRevision } from 'libs/end-to-end/src/types/databases/revisions/database-revision';
import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { TransformCompareOutputSchema } from 'libs/end-to-end/src/types/databases/transform/transform-compare-output';
import { z } from 'zod';
import { OrganizationEntitledDagsterTransforms } from 'libs/end-to-end/src/external-dagster-transforms';
import { OrganizationEntitledDagsterTransform } from 'libs/end-to-end/src/external-dagster-transforms';
import { TransformType } from 'apps/sigma2-ui/src/data-wizard/components/transform-page/TransformEditorUI';

const { uiEnvironmentName, uiSandboxName } = uiEnvironment;

export interface TransformPageHook {
  code: string;
  setCode: Setter<string>;
  isRunningTransform: boolean;
  isLoadingScript: boolean;
  transformId: string | null;
  setTransformId: Setter<string | null>;
  taggedDataSetReferences: TaggedDataSetReference[];
  setTaggedDataSetReferences: Setter<TaggedDataSetReference[]>;
  runOnBatch: boolean;
  setRunOnBatch: Setter<boolean>;
  organizationId: string;
  databaseId: string;
  transforms: TransformReferenceWithOutputConsoleUrl[];
  transformDetails: {
    reference: TransformReferenceWithOutputConsoleUrl;
    transform: Transform;
  } | null;
  mostRecentErrorMessage: string | null;
  continueToSchemaButtonBehavior: TouchTargetBehavior;
  continueToSchemaButtonDisabled: boolean;
  continueToSchemaButtonLabel: string;
  runTransformButtonDisabled: boolean;
  runTransformButtonBehavior: TouchTargetBehavior;
  runTransformButtonLabel: string;
  usePresetCodeButtonDisabled: boolean;
  usePresetCodeButtonBehavior: TouchTargetBehavior;
  usePresetCodeButtonLabel: string;
  viewVisualizationButtonDisabled: boolean;
  viewVisualizationButtonBehavior: TouchTargetBehavior;
  viewVisualizationButtonLabel: string;
  showVisualization: boolean;
  setShowVisualization: Setter<boolean>;
  localTestCommand: string;
  transformType: TransformType;
  setTransformType: Setter<TransformType>;
  isToBeCompared: boolean;
  setIsToBeCompared: Setter<boolean>;
  platformTransformDetails: PlatformTransformDetails | null;
  setPlatformTransformDetails: Setter<PlatformTransformDetails | null>;
  selectedTransformToCompare: string | null;
  setSelectedTransformToCompare: Setter<string | null>;
  transformOptions: string[];
  compareOutputContents: string | null;
  setCompareOutputContents: Setter<string | null>;
  tryReloadCompare: () => void;
  tryReloadDataSummary: () => void;
  dataSummary: string | null;
  entitledDagsterTransforms: OrganizationEntitledDagsterTransform[];
  selectedDagsterTransformId: string | null;
  setSelectedDagsterTransformId: Setter<string | null>;
  currentPage: number;
  setCurrentPage: Setter<number>;
  totalCount: number;
  pageSize: number;
  totalPages: number;
  isTransformsLoading: boolean;
  setTransformsListState: Setter<
    | { status: 'loading' }
    | {
        status: 'done';
        entity: TransformReferencesPaginatedWithOutputConsoleUrls;
      }
    | null
  >;
  onRegisterReloadTransforms: (reload: () => void) => void;
}

const startingCode = `import pandas as pd
import numpy as np
import s2utils

def run_transformation(datasources):
    ...start typing here...
`;

export const presetCode = `import pandas as pd
import numpy as np
import s2utils
from typing import Any

def run_transformation(datasources: dict[str, str]) -> Any:
  # Enter file name here
  file_name = "main_data.csv"
  df_clean = pd.read_csv(datasources[file_name])
  df_clean = s2utils.s2_default_transform(df_clean)

  return df_clean
`;

export const useTransformPage = (): TransformPageHook => {
  const [code, setCode] = useState<string>(startingCode);
  const [isRunningTransform, setIsRunningTransform] = useState<boolean>(false);
  const [isLoadingScript, setIsLoadingScript] = useState<boolean>(false);
  const [showVisualization, setShowVisualization] = useState<boolean>(false);
  const [transformId, setTransformId] = useState<string | null>(null);
  const [taggedDataSetReferences, setTaggedDataSetReferences] = useState<
    TaggedDataSetReference[]
  >([]);

  const [transformDetails, setTransformDetails] = useState<{
    reference: TransformReferenceWithOutputConsoleUrl;
    transform: Transform;
  } | null>(null);

  const [runOnBatch, setRunOnBatch] = useState<boolean>(false);
  const [transformType, setTransformType] = useState<TransformType>('python');
  const [isToBeCompared, setIsToBeCompared] = useState<boolean>(false);
  const [platformTransformDetails, setPlatformTransformDetails] =
    useState<PlatformTransformDetails | null>(null);
  const [selectedTransformToCompare, setSelectedTransformToCompare] = useState<
    string | null
  >(null);
  const [compareOutputContents, setCompareOutputContents] = useState<
    string | null
  >(null);
  const [selectedDagsterTransformId, setSelectedDagsterTransformId] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  type TransformsListState =
    | { status: 'loading' }
    | {
        status: 'done';
        entity: TransformReferencesPaginatedWithOutputConsoleUrls;
      }
    | null;
  const [transformsListState, setTransformsListState] =
    useState<TransformsListState>(null);
  const reloadTransformsRef = React.useRef<(() => void) | null>(null);
  const onRegisterReloadTransforms = useCallback((reload: () => void) => {
    reloadTransformsRef.current = reload;
  }, []);

  useEffect(() => {
    setTransformsListState(null);
  }, [currentPage]);

  // Clear tagged datasets when switching to Dagster transform type
  useEffect(() => {
    if (transformType === 'dagster' && taggedDataSetReferences.length > 0) {
      setTaggedDataSetReferences([]);
    }
  }, [transformType, taggedDataSetReferences.length]);

  const [dataSummary, setDataSummary] = useState<string | null>(null);

  const getDataSummaryReadPattern = useSimpleReadPattern({
    getUrlFn: () =>
      `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/transforms/${transformId}/data-summary`,
    canRead: !!transformId,
    responseSchema: z.object({
      dataSummary: z.string(),
    }),
  });

  const navigate = useNavigateSafe();

  const { organizationId, databaseId } = useParams();
  const { myUser } = useUser();

  if (!organizationId || !databaseId) {
    throw new Error('Missing organizationId or databaseId');
  }

  const userId = useMemo(() => {
    return myUser?.id ?? null;
  }, [myUser]);

  const { upload, canUpload } = useUserUploadHeadless({ userId });

  const compareOutputUrlFn = useCallback(() => {
    if (!transformId) {
      return null;
    }
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/transforms/${transformId}/compare`;
  }, [organizationId, databaseId, transformId]);

  const compareOutput = useSimpleReadPattern({
    getUrlFn: compareOutputUrlFn,
    canRead: !!transformId,
    responseSchema: TransformCompareOutputSchema,
  });

  const tryReloadDataSummary = useCallback(() => {
    if (getDataSummaryReadPattern.status === 'loading') {
      return;
    }

    getDataSummaryReadPattern.reloadEntity();
  }, [getDataSummaryReadPattern]);
  const getEntitledDagsterTransformsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/dagster-transforms`;
  }, [organizationId]);

  const entitledDagsterTransformsPattern = useSimpleReadPattern({
    getUrlFn: getEntitledDagsterTransformsUrlFn,
    canRead: true,
    responseSchema: OrganizationEntitledDagsterTransforms,
  });

  const entitledDagsterTransforms = useMemo(() => {
    return entitledDagsterTransformsPattern.status === 'done' &&
      entitledDagsterTransformsPattern.entity
      ? entitledDagsterTransformsPattern.entity
      : [];
  }, [entitledDagsterTransformsPattern]);

  const tryReloadCompare = useCallback(() => {
    if (compareOutput.status === 'loading') {
      return;
    }

    compareOutput.reloadEntity();
  }, [compareOutput]);

  useEffect(() => {
    if (compareOutput.status === 'done' && compareOutput.entity) {
      setCompareOutputContents(JSON.stringify(compareOutput.entity));
    } else if (compareOutput.status === 'error') {
      setCompareOutputContents(null);
    }
  }, [compareOutput]);

  useEffect(() => {
    if (
      getDataSummaryReadPattern.status === 'done' &&
      getDataSummaryReadPattern.entity
    ) {
      setDataSummary(getDataSummaryReadPattern.entity.dataSummary);
    } else if (getDataSummaryReadPattern.status === 'error') {
      setDataSummary(null);
    }
  }, [getDataSummaryReadPattern]);

  const transforms: TransformReferenceWithOutputConsoleUrl[] = useMemo(() => {
    return transformsListState?.status === 'done'
      ? transformsListState.entity.transforms
      : [];
  }, [transformsListState]);

  const totalCount = useMemo(() => {
    return transformsListState?.status === 'done'
      ? transformsListState.entity.totalCount
      : 0;
  }, [transformsListState]);

  const pageSize = useMemo(() => {
    return transformsListState?.status === 'done'
      ? transformsListState.entity.pageSize
      : 10;
  }, [transformsListState]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize],
  );

  const isTransformsLoading =
    transformsListState === null || transformsListState.status === 'loading';

  const transformOptions = transforms.map((t) => t.id);

  const tryReloadTransforms = useCallback(() => {
    reloadTransformsRef.current?.();
  }, []);

  const [mostRecentErrorMessage, setMostRecentErrorMessage] = React.useState<
    string | null
  >(null);

  const { myUserStatus, userAuthenticatedApiCall } = useUser();
  useAutoloadScript({
    organizationId,
    databaseId,
    userAuthenticatedApiCall,
    myUserStatus,
    setCode,
    transformId,
    setIsLoadingScript,
    setMostRecentErrorMessage,
    setTaggedDataSetReferences,
    transforms,
    setRunOnBatch,
    setTransformType,
    setIsToBeCompared,
    setPlatformTransformDetails,
    setSelectedTransformToCompare,
    setSelectedDagsterTransformId,
    setTransformDetails,
  });

  const createTransformGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/transforms`;
  }, [databaseId, organizationId]);

  const createTransformHook = useSimpleWritePattern({
    getUrlFn: createTransformGetUrlFn,
    writeSchema: CreateTransformRequestBodySchema,
    method: 'POST',
    canWrite: true,
    responseSchema: Transform,
  });

  const pollForTransformUpdates = useAsyncCallback(
    async (isAlive: IsAliveFn, newTransformId: string) => {
      try {
        let isFirstPoll = true;

        while (isAlive()) {
          await waitForMs(isFirstPoll ? 500 : 3000);
          isFirstPoll = false;

          const getStatusResult = await userAuthenticatedApiCall({
            url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/transforms/${newTransformId}`,
            requestOptions: {
              method: 'GET',
            },
            responseSchema: Transform,
          });

          if (!getStatusResult || getStatusResult.resultType === 'err') {
            setMostRecentErrorMessage('Failed to get execution status');
            setIsRunningTransform(false);
            return;
          }
          const result = getStatusResult.value;

          if (
            result.details.status === 'Succeeded' ||
            result.details.status === 'Failed'
          ) {
            setIsRunningTransform(false);
            setMostRecentErrorMessage(null);
            return;
          } else if (
            result.details.status === 'Running' ||
            result.details.status === 'NotStarted'
          ) {
            continue;
          }

          throw guardExhaustive(result.details);
        }
      } finally {
        setIsRunningTransform(false);
        tryReloadTransforms();
      }
    },
    [databaseId, organizationId, tryReloadTransforms, userAuthenticatedApiCall],
  );

  const runTransform = useCallback(() => {
    if (createTransformHook.status === 'saving' || !canUpload) {
      return;
    }

    setIsRunningTransform(true);

    const executeTransform = (userUploadId: string) => {
      createTransformHook.saveEntity(
        {
          dataSetReferences: taggedDataSetReferences,
          scriptUserUploadId: userUploadId,
          runOnBatch,
          runOnPlatformTransform: transformType === 'platform',
          platformTransformDetails: platformTransformDetails,
          transformIdToCompare: selectedTransformToCompare,
          runOnDagsterTransform: transformType === 'dagster',
          dagsterTransformId: selectedDagsterTransformId,
        },
        (transform) => {
          pollForTransformUpdates(transform.id);
        },
      );
    };

    if (transformType === 'platform' || transformType === 'dagster') {
      executeTransform(userId ?? '');
    } else {
      upload(
        new Blob([code], { type: 'text/plain' }),
        executeTransform,
        (error) => {
          setIsRunningTransform(false);
          setMostRecentErrorMessage(error);
        },
      );
    }
  }, [
    platformTransformDetails,
    userId,
    transformType,
    createTransformHook,
    canUpload,
    upload,
    code,
    taggedDataSetReferences,
    runOnBatch,
    selectedTransformToCompare,
    selectedDagsterTransformId,
    pollForTransformUpdates,
  ]);

  const writeRevisionGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions?transformId=${transformId}`;
  }, [databaseId, organizationId, transformId]);

  const writeRevisionPattern = useSimpleWritePattern({
    responseSchema: DatabaseRevision,
    canWrite: true,
    writeSchema: EmptyObject,
    method: 'POST',
    getUrlFn: writeRevisionGetUrlFn,
  });

  const localTestCommand = useMemo(() => {
    if (!transformId || taggedDataSetReferences.length <= 0) {
      return '';
    }
    if (transformType === 'python') {
      const dataSetsCommandString = taggedDataSetReferences
        .map(
          (ds) =>
            `${ds.automationTag}/${ds.dataSetReference.dataSetId}/${ds.dataSetReference.dataSetVersion}`,
        )
        .join(' ');
      return `S2_ENVIRONMENT_NAME="${uiEnvironmentName}" S2_SANDBOX_NAME="${uiSandboxName}" AWS_PROFILE="${uiEnvironmentName}" pnpm tool local-transform --org-id ${organizationId} --db-id ${databaseId} --transform-id ${transformId} ${dataSetsCommandString}`;
    } else if (transformType === 'platform') {
      return `S2_ENVIRONMENT_NAME="${uiEnvironmentName}" S2_SANDBOX_NAME="${uiSandboxName}" AWS_PROFILE="${uiEnvironmentName}" pnpm tool local-platform-transform --org-id ${organizationId} --db-id ${databaseId} --transform-id ${transformId}`;
    } else {
      return '';
    }
  }, [
    databaseId,
    organizationId,
    taggedDataSetReferences,
    transformId,
    transformType,
  ]);

  return useMemo(() => {
    return {
      code,
      setCode,
      isRunningTransform,
      isLoadingScript,
      transformId,
      taggedDataSetReferences,
      organizationId,
      databaseId,
      transforms,
      mostRecentErrorMessage,
      setTaggedDataSetReferences,
      setTransformId,
      continueToSchemaButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (writeRevisionPattern.status === 'saving') {
            return;
          }

          writeRevisionPattern.saveEntity({}, (newRevision) => {
            navigate(
              `/organizations/${organizationId}/databases/${databaseId}/revisions/${newRevision.id}/schema`,
            );
          });
        },
      },
      continueToSchemaButtonDisabled: !transformId || isRunningTransform,
      continueToSchemaButtonLabel: 'Continue to schema',
      runTransformButtonLabel: 'Run transform',
      runTransformButtonDisabled:
        isRunningTransform ||
        (transformType === 'python' && taggedDataSetReferences.length <= 0) ||
        (transformType === 'platform' &&
          (!platformTransformDetails || taggedDataSetReferences.length <= 0)) ||
        (transformType === 'dagster' && !selectedDagsterTransformId),
      runTransformButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          runTransform();
        },
      },
      usePresetCodeButtonLabel: 'Use preset code',
      usePresetCodeButtonDisabled: isRunningTransform,
      usePresetCodeButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          setCode(presetCode);
        },
      },
      viewVisualizationButtonLabel: 'View Flowchart',
      viewVisualizationButtonDisabled: false,
      viewVisualizationButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          setShowVisualization((prev) => !prev);
        },
      },
      showVisualization,
      setRunOnBatch,
      runOnBatch,
      localTestCommand,
      transformType,
      setTransformType,
      isToBeCompared,
      setIsToBeCompared,
      platformTransformDetails,
      setPlatformTransformDetails,
      setShowVisualization,
      selectedTransformToCompare,
      setSelectedTransformToCompare,
      transformOptions,
      compareOutputContents,
      setCompareOutputContents,
      tryReloadCompare,
      dataSummary,
      tryReloadDataSummary,
      entitledDagsterTransforms,
      selectedDagsterTransformId,
      setSelectedDagsterTransformId,
      transformDetails,
      currentPage,
      setCurrentPage,
      totalCount,
      pageSize,
      totalPages,
      isTransformsLoading,
      setTransformsListState,
      onRegisterReloadTransforms,
    };
  }, [
    code,
    transformDetails,
    showVisualization,
    isRunningTransform,
    isLoadingScript,
    transformId,
    taggedDataSetReferences,
    organizationId,
    databaseId,
    transforms,
    mostRecentErrorMessage,
    runOnBatch,
    localTestCommand,
    writeRevisionPattern,
    navigate,
    runTransform,
    transformType,
    isToBeCompared,
    platformTransformDetails,
    setPlatformTransformDetails,
    selectedTransformToCompare,
    transformOptions,
    compareOutputContents,
    setCompareOutputContents,
    tryReloadCompare,
    tryReloadDataSummary,
    dataSummary,
    entitledDagsterTransforms,
    selectedDagsterTransformId,
    currentPage,
    totalCount,
    pageSize,
    totalPages,
    isTransformsLoading,
    setTransformsListState,
    onRegisterReloadTransforms,
  ]);
};

/**
 * Fetches the transforms list for a single page. Key by currentPage in the parent
 * so each page gets a fresh mount and exactly one initial load (no reloadEntity).
 */
export const TransformListFetcher = ({
  organizationId,
  databaseId,
  page,
  onStateChange,
  onRegisterReloadTransforms,
}: {
  organizationId: string;
  databaseId: string;
  page: number;
  onStateChange: (
    state:
      | { status: 'loading' }
      | {
          status: 'done';
          entity: TransformReferencesPaginatedWithOutputConsoleUrls;
        }
      | null,
  ) => void;
  onRegisterReloadTransforms: (reload: () => void) => void;
}) => {
  const getUrlFn = useCallback(
    () =>
      `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/transforms?page=${page}`,
    [organizationId, databaseId, page],
  );
  const pattern = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: TransformReferencesPaginatedWithOutputConsoleUrls,
  });

  useEffect(() => {
    if (pattern.status === 'loading') {
      onStateChange({ status: 'loading' });
      return;
    }
    if (pattern.status === 'done' && pattern.entity) {
      onStateChange({ status: 'done', entity: pattern.entity });
    }
  }, [pattern, onStateChange]);

  useEffect(() => {
    if (
      (pattern.status === 'done' || pattern.status === 'error') &&
      'reloadEntity' in pattern
    ) {
      onRegisterReloadTransforms(pattern.reloadEntity);
    }
  }, [pattern, onRegisterReloadTransforms]);

  return null;
};

const useAutoloadScript = ({
  setIsLoadingScript,
  setMostRecentErrorMessage,
  setCode,
  myUserStatus,
  transformId,
  userAuthenticatedApiCall,
  databaseId,
  organizationId,
  setTaggedDataSetReferences,
  setRunOnBatch,
  transforms,
  setTransformType,
  setIsToBeCompared,
  setPlatformTransformDetails,
  setSelectedTransformToCompare,
  setSelectedDagsterTransformId,
  setTransformDetails,
}: {
  transformId: string | null;
  databaseId: string;
  organizationId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  myUserStatus: UserStatus;
  setIsLoadingScript: Setter<boolean>;
  setMostRecentErrorMessage: Setter<string | null>;
  setCode: Setter<string>;
  setTaggedDataSetReferences: Setter<TaggedDataSetReference[]>;
  setRunOnBatch: Setter<boolean>;
  transforms: TransformReferenceWithOutputConsoleUrl[];
  setTransformType: Setter<TransformType>;
  setIsToBeCompared: Setter<boolean>;
  setPlatformTransformDetails: Setter<PlatformTransformDetails | null>;
  setSelectedTransformToCompare: Setter<string | null>;
  setSelectedDagsterTransformId: Setter<string | null>;
  setTransformDetails: Setter<{
    reference: TransformReferenceWithOutputConsoleUrl;
    transform: Transform;
  } | null>;
}) => {
  useAsyncEffect(
    async (isAlive: IsAliveFn) => {
      if (!isAlive() || myUserStatus !== 'logged_in' || !transformId) {
        return;
      }

      const transformReference = transforms.find((t) => t.id === transformId);

      if (!transformReference) {
        return;
      }

      setIsLoadingScript(true);

      try {
        const transformResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/transforms/${transformId}`,
          requestOptions: {
            method: 'GET',
          },
          responseSchema: Transform,
        });

        if (!transformResult || transformResult.resultType === 'err') {
          setMostRecentErrorMessage('Failed to load transform');
          return;
        }

        const transform = transformResult.value;

        // Determine transform type from the transform object
        let type: TransformType = 'python';
        if (transform.runOnDagsterTransform) {
          type = 'dagster';
        } else if (transform.runOnPlatformTransform) {
          type = 'platform';
        }

        if (type === 'python') {
          const scriptResult = await userAuthenticatedApiCall({
            url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/transforms/${transformId}/script`,
            requestOptions: {
              method: 'GET',
            },
            responseSchema: TransformScriptContents,
          });

          if (!scriptResult || scriptResult.resultType === 'err') {
            setMostRecentErrorMessage('Failed to load script');
            return;
          }
          setCode(scriptResult.value.contents);
        }

        setTransformDetails({
          reference: transformReference,
          transform: transform,
        });

        setTaggedDataSetReferences(transform.dataSetReferences);
        setRunOnBatch(transform.runOnBatch ?? false);
        setTransformType(type);
        setPlatformTransformDetails(transform.platformTransformDetails);
        setIsToBeCompared(!!transform.transformIdToCompare);
        setSelectedTransformToCompare(transform.transformIdToCompare);
        setSelectedDagsterTransformId(transform.dagsterTransformId ?? null);
      } finally {
        setIsLoadingScript(false);
      }
    },
    [
      databaseId,
      myUserStatus,
      transforms,
      organizationId,
      setCode,
      setIsLoadingScript,
      setMostRecentErrorMessage,
      transformId,
      userAuthenticatedApiCall,
      setTaggedDataSetReferences,
      setRunOnBatch,
      setTransformType,
      setIsToBeCompared,
      setPlatformTransformDetails,
      setSelectedTransformToCompare,
      setSelectedDagsterTransformId,
      setTransformDetails,
    ],
  );
};
